import ServiceLayout from "@/components/ServiceLayout";
import { AlertCircle } from "lucide-react";

export default function Marketing() {
  return (
    <ServiceLayout
      slug="marketing-website-design"
      eyebrow="Solutions · Marketing & Website Design"
      title="A stronger digital presence. More opportunities to grow."
      intro="Your website works when you're not in the room. Through our Marketing & Website Design offering, delivered with Veluro, Riverstone helps businesses build a modern online presence, reach more of the right customers, and follow up like a much larger company."
      image="https://images.unsplash.com/photo-1637502875124-eb4a9843a2fa?q=80&w=1600&auto=format&fit=crop"
      imageAlt="Laptop on a wooden desk used for planning a modern website"
      ctaLabel="Discuss My Marketing Goals"
      providerLink={{ href: "https://veluro.ai", label: "Learn more about Veluro at veluro.ai" }}
      whoFor={[
        "Businesses that want more of the right customers to find them.",
        "Companies whose website no longer reflects the quality of their work.",
        "Teams without the time or staff to run marketing consistently.",
        "Organizations ready to turn their online presence into a source of inquiries.",
      ]}
      problems={[
        "An outdated or slow website that quietly turns visitors away.",
        "Low visibility when buyers search for what you do.",
        "Leads that go cold because follow-up is inconsistent.",
        "Content and advertising that happen in bursts instead of a plan.",
      ]}
      capabilities={[
        { title: "Modern, responsive websites", body: "Fast, mobile-first sites that make the right first impression." },
        { title: "Landing pages & lead-generation funnels", body: "Focused pages built to convert interest into inquiries." },
        { title: "Digital advertising", body: "Campaigns placed where your buyers actually spend attention." },
        { title: "Social media content & planning", body: "A steady, on-brand rhythm instead of last-minute posts." },
        { title: "AI-assisted marketing workflows", body: "Practical automation that saves time without losing the human touch." },
        { title: "Email follow-up & lead nurturing", body: "Systematic follow-up so fewer opportunities slip away." },
        { title: "Search visibility", body: "Foundational search engine optimization so buyers can find you." },
        { title: "Reporting & optimization", body: "Clear reporting on what’s working, refined over time." },
      ]}
      providerRole={[
        {
          title: "Veluro’s role — delivery",
          body: "Veluro — your AI-powered growth team — is the marketing and website delivery partner for this solution. Their team finds high-intent buyers, launches campaigns across every channel, builds modern websites, and books meetings, with reporting that watches the health of your business. Visit veluro.ai to learn more.",
        },
        {
          title: "Riverstone’s role — connection & continuity",
          body: "Riverstone Partners introduces you to Veluro, helps articulate your goals, and remains a point of contact as the engagement begins. We don’t build the website or run the campaigns ourselves.",
        },
      ]}
      conversation={[
        { title: "Your goals", body: "Where you are today—your site, your visibility, your pipeline—and what growth would look like." },
        { title: "Gaps & opportunities", body: "A candid look at what’s working, what isn’t, and which capabilities could move the needle." },
        { title: "Scope & next steps", body: "What a package would include, what it wouldn’t, and how the engagement would begin." },
      ]}
      faqs={[
        { q: "Do we have to rebuild our whole website?", a: "Not necessarily. Some engagements start with a single landing page, a funnel, or a campaign. Scope is set after an honest review of what you have." },
        { q: "Who does the actual work?", a: "Veluro’s team delivers the marketing and website work. Riverstone makes the introduction, helps frame your goals, and stays engaged." },
        { q: "What will it cost?", a: "It depends on scope. Packages are confirmed with the delivery team during your initial conversation—we don’t quote one-size-fits-all prices." },
        { q: "Do you guarantee results or revenue growth?", a: "No. We don’t promise specific rankings, traffic numbers, or revenue outcomes, and we’re cautious of anyone who does." },
        { q: "How long does a project take?", a: "Timelines depend on scope. You’ll receive a schedule with your proposal before anything begins." },
      ]}
      extra={
        <p className="mt-14 flex items-start gap-3 border border-charcoal/10 bg-sage/40 p-6 text-sm leading-relaxed text-charcoal/70">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-forest" />
          The list above describes the capabilities available through this offering. The
          final package for your engagement—exactly what’s included—is confirmed with the
          delivery team during your initial conversation.
        </p>
      }
    />
  );
}
